import { Dispatch, useEffect, useMemo, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import iconInbox from 'assets/img/icon-bell.svg';
import classNames from 'classnames';
import { Action } from 'redux';

import lang from '../../../lib/language';
import { RootState } from '../../../lib/store';
import {
  getAllMessages,
  markAllMessagesAsRead,
  setAllMessagesPageToReadToInitial,
} from '../../../lib/store/ams';
import { hasAccountPaidAndDemoPlan } from '../../../lib/store/crm/helpers';
import { FeatureFlags } from '../../../lib/util/flags/types';

import { useFlags, useIsMobile } from '../../../hooks';
import useAnimatedMount, { AnimatedMountStatus } from '../../../shared_components/hooks/useAnimatedMount';
import useClickOutside from '../../../shared_components/hooks/useClickOutside';
import { UI_ROUTES } from '../../../util/routes';
import { ClickableDiv, UserDetails, UserPhoto } from '../../index';
import SwitchingAccountPopup from '../../SwitchingAccountPopup/SwitchingAccountPopup';
import { HeaderDropdownItem, MyProfileOptions } from '../Header.types';
import { LogoutActionSelector } from '../HeaderConstants';
import ModalChangePassword from '../HeaderDropdownNav/myProfile/modalChangePassword/modal/ModalChangePassword';
import MyProfile from '../HeaderDropdownNav/myProfile/MyProfile';
import Notifications from '../HeaderDropdownNav/notifications/Notifications';

import styles from './HeaderDropdown.module.scss';

type OwnProps = {
  isUserProfile: boolean;
  actions?: {};
};

type Props = OwnProps & ReduxProps;

const HeaderDropdown = (props: Props) => {
  const {
    isUserProfile,
    actions,
    allMessages,
    hasUnreadMessages,
    individual,
    unreadCount,
    putAllMessagesAsRead,
    setPageToReadToInitial,
    getAllDataMessages,
    hasPaidAndDemoPlan,
    isUseDemoAccount,
  } = props;
  const { isMobile } = useIsMobile();
  const flags = useFlags([ FeatureFlags.referrals ]);
  const isReferralsEnabled = flags[FeatureFlags.referrals].enabled;
  const navigate = useNavigate();
  const location = useLocation();
  let locationState = location.state as { dropDownItem: string } | null;

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isInitialStateDropdown, setIsInitialStateDropdown] = useState<boolean>(false);
  const [isSwitchingOtherAccount, setIsSwitchingOtherAccount] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isMounted: isMenuMounted, status: menuStatus } = useAnimatedMount(isDropdownOpen);

  useClickOutside(dropdownRef, () => setIsDropdownOpen(false));

  const toggle = () => {
    if (isMobile) {
      navigate(
        {
          pathname: `${UI_ROUTES.myAccountNotifications}`,
        },
        { state: { messageId: null, isViewAll: true } },
      );
      return;
    }
    setIsDropdownOpen((prev) => !prev);
  };

  const handleClickIconInbox = () => {
    setPageToReadToInitial();
    getAllDataMessages();
  };

  const handleToggleClick = () => {
    setIsInitialStateDropdown(true);
    toggle();
  };

  const dropdownItemsMyProfile: HeaderDropdownItem[] = useMemo(() => {
    let items: HeaderDropdownItem[] = [
      {
        id: 1,
        link: `${UI_ROUTES.myAccountPersonalDetails}`,
        text: lang.mobileProfilePersonalDetails(),
      },
      ...(isReferralsEnabled
        ? [{ id: MyProfileOptions.PAYOUTS, link: `${UI_ROUTES.payouts}`, text: lang.commonPayoutsTitle() }]
        : []),
      { id: 4, link: '', text: lang.mobileProfileChangePassword() },
      {
        id: 5,
        link: '/',
        text: lang.commonLogout(),
        alignBottom: true,
        action: actions ? actions[LogoutActionSelector] : {},
      },
    ];

    if (hasPaidAndDemoPlan) {
      items = [
        {
          id: 0,
          link: '',
          text: isUseDemoAccount ? lang.commonSwitchToFunded() : lang.commonSwitchToDemo(),
        },
        ...items,
      ];
    }

    return items;
  }, [actions, hasPaidAndDemoPlan, isUseDemoAccount, isReferralsEnabled]);

  const getDropdownToggle = () => {
    if (!isUserProfile) {
      return (
        <ClickableDiv
          onClick={() => {
            handleClickIconInbox();
            handleToggleClick();
          }}
          aria-expanded={isDropdownOpen}
          className={isMobile ? styles.notificationsMobile : styles.notifications}
        >
          <div className={styles.inboxWrapper}>
            <div className={styles.inbox}>
              {hasUnreadMessages ? (
                <>
                  <div className={styles.circleUnreadMessages}>{unreadCount}</div>
                  <img src={iconInbox} alt="bell" />
                </>
              ) : (
                <img src={iconInbox} alt="bell" />
              )}
            </div>
          </div>
        </ClickableDiv>
      );
    }

    return (
      <ClickableDiv
        className={classNames(styles.profile, { [styles.profileActive]: isDropdownOpen })}
        aria-expanded={isDropdownOpen}
        onClick={handleToggleClick}
      >
        <UserPhoto isHeader first_name={individual?.first_name} last_name={individual?.last_name} />
        <UserDetails />
      </ClickableDiv>
    );
  };

  const handleBtnMarkAllAsRead = () => {
    putAllMessagesAsRead();
  };

  const handleClickBtnDropDown = () => {
    setIsDropdownOpen(false);
  };

  if (!isUserProfile || isInitialStateDropdown) {
    locationState = null;
  }

  useEffect(() => {
    if (!isDropdownOpen) {
      return undefined;
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);

    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDropdownOpen]);

  return (
    <div ref={dropdownRef} className={styles.dropdownWrapper}>
      {getDropdownToggle()}
      {isMenuMounted && (
        <div
          className={classNames(
            styles.dropdownMenu,
            isUserProfile ? styles.dropdown : styles.dropdownNotifications,
            menuStatus === AnimatedMountStatus.Open ? styles.dropdownMenuOpen : styles.dropdownMenuClosing,
          )}
        >
          {isUserProfile ? (
            <MyProfile
              dropdownItemsMyProfile={dropdownItemsMyProfile}
              dropDownItem={locationState ? locationState.dropDownItem : null}
              handleClickBtnDropDown={handleClickBtnDropDown}
              setIsSwitchingOtherAccount={setIsSwitchingOtherAccount}
              openChangePasswordModal={() => setIsChangePasswordOpen(true)}
            />
          ) : (
            <Notifications
              handleBtnMarkAllAsRead={handleBtnMarkAllAsRead}
              allMessages={allMessages}
              hasUnreadMessages={hasUnreadMessages}
              handleClose={() => setIsDropdownOpen(false)}
            />
          )}
        </div>
      )}
      {isUserProfile && (
        <ModalChangePassword
          isOpen={isChangePasswordOpen}
          onClose={() => setIsChangePasswordOpen(false)}
        />
      )}
      {isSwitchingOtherAccount && (
        <SwitchingAccountPopup
          isDisplayModal={isSwitchingOtherAccount}
          setIsDisplayModal={setIsSwitchingOtherAccount}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  allMessages: state.ams.allMessages,
  hasUnreadMessages: state.ams.hasUnreadMessages,
  unreadCount: state.ams.unreadCount,
  individual: state.crm.individualExtendedInfo,
  hasPaidAndDemoPlan: hasAccountPaidAndDemoPlan(state.crm),
  isUseDemoAccount: state.crm.isUseDemoAccount,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  putAllMessagesAsRead: () => dispatch(markAllMessagesAsRead()),
  setPageToReadToInitial: () => dispatch(setAllMessagesPageToReadToInitial()),
  getAllDataMessages: () => dispatch(getAllMessages()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connect(mapStateToProps, mapDispatchToProps)(HeaderDropdown);
